"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Layers, FileCheck, HardHat, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const capabilitiesData = [{
  id: "cap-1",
  title: "Protective scaffolds and debris netting",
  description: "Heavy-duty containment screening and close-boarded protection to shield public walkways, adjacent properties, and work crews below from falling material.",
  icon: ShieldCheck,
  tag: "Containment & Protection"
}, {
  id: "cap-2",
  title: "Phased access for staged demolition",
  description: "Progressive dismantling and progressive floor-by-floor access structures adapted continuously as building heights and load profiles change.",
  icon: Layers,
  tag: "Staged Access"
}, {
  id: "cap-3",
  title: "Edge protection",
  description: "Compliant double guardrails, intermediate rails, and fitted toe-boards engineered for leading edges, open slab boundaries, and roof perimeters.",
  icon: HardHat,
  tag: "Fall Prevention"
}, {
  id: "cap-4",
  title: "Site-specific RAMS",
  description: "Detailed risk assessments and method statements tailored to the specific ground conditions, access constraints, and trades on your site.",
  icon: FileCheck,
  tag: "Safety & Compliance"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export default function Capabilities() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="relevantcapabilities" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Left Column: Context & Overview */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border text-foreground font-medium px-3 py-1">
                Technical Capabilities
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Relevant capabilities for complex access
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              We engineer each access structure around strict safety standards, site constraints, and the exact sequence of your works across the South East and Home Counties.
            </p>

            <div className="p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col gap-4 mt-2">
              <h3 className="font-semibold text-card-foreground text-base flex items-center gap-2">
                <CheckCircle2 className="size-5 text-primary shrink-0" />
                <span>Standard on every deployment</span>
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2.5">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <span>NASC, CHAS, and Constructionline compliant workflows</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <span>Routine weekly scaffold inspections and handover tagging</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <span>Direct supervision by experienced scaffolding managers</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/HealthSafetyAccreditationsPage">
                  View safety standards
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Interactive Bullet Cards */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial={prefersReducedMotion ? "visible" : "hidden"} whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {capabilitiesData.map((item, index) => {
              const IconComponent = item.icon;
              return <motion.div key={item.id} data-index={index} variants={itemVariants} className="h-full">
                    <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 border">
                      <CardContent className="p-6 flex flex-col justify-between h-full gap-4">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                              <IconComponent className="size-5" />
                            </div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {item.tag}
                            </span>
                          </div>

                          <h3 className="text-lg font-semibold text-card-foreground leading-snug">
                            {item.title}
                          </h3>

                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>

                        <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                          <span>BMF Standard Practice</span>
                          <CheckCircle2 className="size-4 text-primary" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}